import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Snackbar, Alert, Typography } from '@mui/material';

interface SnackbarContext {
  show: (title: string | null, message: string | null, severity?: 'success' | 'error' | 'info' | 'warning') => void;
}

const SnackbarContext = createContext<SnackbarContext | undefined>(undefined);

export const useSnackbar = (): SnackbarContext => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [severity, setSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [message, setMessage] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const show = (title: string | null, message: string | null, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setTitle(title);
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
  };

  return (
    <SnackbarContext.Provider value={{ show }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            mr: 3,
            minWidth: '400px',
            maxWidth: '600px',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: 3,
          },
        }}
      >
        <Alert onClose={handleClose} severity={severity} variant="filled" sx={{ padding: '24px 32px', lineHeight: 1.6 }}>
          {title && <Typography sx={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>{title}</Typography>}
          {message && <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>{message}</Typography>}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
